import { useState } from "react";
import { submitFeedback } from "../services/feedbackService";

interface FeedbackWidgetProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackWidget = ({ open, onClose }: FeedbackWidgetProps) => {
  const [rating, setRating] = useState<number>(0);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setError("");
    if (sent) {
      setSent(false);
      setRating(0);
      setMessage("");
    }
    onClose();
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setError("Please write your feedback.");
      return;
    }
    setError("");
    setSending(true);
    try {
      await submitFeedback({
        rating: rating > 0 ? rating : undefined,
        message: message.trim(),
      });
      setSent(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not send your feedback. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md text-white">
        {sent ? (
          <div className="text-center">
            <h3 className="text-xl font-bold text-purple-400 mb-2">
              Thank you!
            </h3>
            <p className="text-gray-300 text-sm mb-5">
              Your feedback helps us make TubeScript AI better.
            </p>
            <button
              onClick={handleClose}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-semibold transition"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-bold mb-1">Send us your feedback</h3>
            <p className="text-gray-400 text-sm mb-4">
              What do you like, and what should we improve?
            </p>

            <label className="block text-gray-300 text-sm font-medium mb-2">
              Rating (optional)
            </label>
            <div className="flex gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n === rating ? 0 : n)}
                  className={`text-2xl transition ${
                    n <= rating
                      ? "text-yellow-400"
                      : "text-gray-600 hover:text-gray-400"
                  }`}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >
                  ★
                </button>
              ))}
            </div>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your feedback here..."
              rows={5}
              maxLength={2000}
              className="w-full bg-gray-900 border border-gray-700 text-white p-3 rounded-xl focus:outline-none focus:border-purple-500 transition text-sm resize-y"
            />
            <p className="text-xs text-gray-500 mt-1 text-right">
              {message.length} / 2000
            </p>

            {error && (
              <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-2 rounded-lg text-sm mt-3">
                {error}
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleClose}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 text-white py-3 rounded-xl font-semibold text-sm transition"
              >
                {sending ? "Sending..." : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackWidget;
